/**
 * ฟอร์มเปลี่ยนรหัสผ่าน — ตัวเดียวสำหรับสองเส้นทางที่ห้ามสับสนกัน
 *
 * ทั้งสองเส้นทางยิง `POST /v1/me/password` ด้วย body เดียวกัน ต่างกันที่ **token ที่
 * ใช้** และ **ตอนจบ** ซึ่งเป็นความต่างของเซิร์ฟเวอร์ ไม่ใช่ของหน้าจอ:
 *
 * | | `RECOVERY` | `VOLUNTARY` |
 * |---|---|---|
 * | ทางเข้า | `403 PASSWORD_CHANGE_REQUIRED` ตอน login | ผู้ใช้ที่เข้าระบบแล้วกดเอง |
 * | token | `password_change_token` (D-050) | session token ปกติ |
 * | หลังสำเร็จ | เซิร์ฟเวอร์ฆ่า **ทุก** session รวมทั้ง token ที่เพิ่งใช้ | session ของผู้เรียก **ยังอยู่** |
 * | ตอนจบ | กลับไปหน้าเข้าสู่ระบบ | อยู่ที่เดิม ยังเข้าระบบอยู่ |
 *
 * component นี้จึงไม่ตัดสินตอนจบเอง — มันเรียก `onChanged()` แล้วปล่อยให้ผู้ใช้งาน
 * component (หน้า Login หรือ shell) เป็นคนพาไปต่อ การฝังตอนจบไว้ที่นี่จะทำให้
 * เส้นทางหนึ่งได้พฤติกรรมของอีกเส้นทางในวันที่มีคนแก้โค้ดโดยไม่รู้ว่ามีสองแบบ
 *
 * สิ่งที่ไฟล์นี้ตั้งใจ **ไม่** ทำ:
 * - ไม่เก็บรหัสผ่านไว้ใน state ของ React เลย ทุกช่องเป็น uncontrolled input และถูก
 *   ล้างทันทีที่ submit จบ รหัสผ่านจึงมีอายุเท่ากับการเรียก `changePassword()` ครั้งนั้น
 * - ไม่ตรวจนโยบายรหัสผ่านฝั่ง client แทนเซิร์ฟเวอร์ — D-051 ย้ายค่านโยบายไปไว้ที่
 *   `Settings` เพราะมันปรับได้โดยไม่ปล่อย release การ validate ซ้ำที่นี่จะกลายเป็น
 *   กติกาคนละชุดทันทีที่ค่าฝั่งเซิร์ฟเวอร์ถูกปรับ ที่นี่จึง**บอกกติกาไว้ล่วงหน้า**
 *   แล้วให้ `422` ของเซิร์ฟเวอร์เป็นผู้ตัดสินจริง
 * - ไม่นับจำนวนครั้งที่พิมพ์รหัสเดิมผิด และไม่พูดถึงการถูกล็อกบัญชี — endpoint นี้
 *   **ไม่นับ lockout** (`docs/API_CONTRACT.md`) การเตือนเรื่องนั้นจึงเป็นคำเท็จที่
 *   ผลักให้คนเลิกลองแล้วไปตามผู้ดูแลระบบแทน
 */

import { useRef, useState, type FormEvent } from 'react';

import type { DeviceKind } from './client';
import './changePassword.css';
import {
  PASSWORD_NEEDS_SPECIAL_CHARACTER_RULE,
  PASSWORD_SAME_AS_CURRENT_RULE,
  PASSWORD_TOO_SHORT_RULE,
  changePassword,
  type PasswordChangeResult,
} from './passwordClient';
import { primaryTouchTargetStyle, touchTargetStyle } from '../../scanner/touch';

/**
 * ความยาวขั้นต่ำที่เอาไว้**บอกผู้ใช้ล่วงหน้า**เท่านั้น — ที่เดียวในฝั่ง frontend
 *
 * mirror ของ `Settings.password_min_length` ฝั่ง API (D-051) ซึ่งเป็นเจ้าของค่าจริง
 * และปรับได้โดยไม่ต้องแก้ไฟล์นี้ ค่าที่นี่จึงเป็นคำแนะนำ ไม่ใช่กติกา — ผู้ตัดสินคือ
 * `422` ของเซิร์ฟเวอร์เสมอ และข้อความของ `422` ด้านล่าง**ไม่ทวนตัวเลขนี้ซ้ำ** เพื่อ
 * ไม่ให้หน้าจอยืนยันตัวเลขที่อาจไม่ตรงกับ deployment นั้นแล้ว
 */
export const PASSWORD_MIN_LENGTH_HINT = 9;

/** ทางเข้าของการเปลี่ยนรหัสผ่าน — ดูตารางใน doc ของโมดูล */
export type PasswordChangePurpose = 'RECOVERY' | 'VOLUNTARY';

/**
 * ความล้มเหลวที่ฟอร์มนี้แสดง
 *
 * สองแบบแรกไม่ใช่คำตอบของเซิร์ฟเวอร์และไม่เคยถูกส่งออกไปเป็นคำขอ — แยกออกจาก
 * `PasswordChangeResult` เพื่อไม่ให้ปนกับ `bad-request` ที่แปลว่าเซิร์ฟเวอร์ปฏิเสธ
 * รูปแบบคำขอจริง ๆ
 */
type ChangeFailure =
  | { readonly kind: 'missing-input' }
  | { readonly kind: 'mismatch' }
  | { readonly kind: 'result'; readonly result: PasswordChangeResult };

export const MISSING_INPUT_MESSAGE =
  'กรอกรหัสผ่านปัจจุบัน รหัสผ่านใหม่ และการยืนยันให้ครบก่อนกดบันทึก';

export const MISMATCH_MESSAGE = 'รหัสผ่านใหม่กับการยืนยันไม่ตรงกัน — พิมพ์ใหม่ทั้งสองช่อง';

/**
 * `401` ของ endpoint นี้
 *
 * แปลว่ารหัสผ่านปัจจุบันผิด **หรือ** token ที่ยื่นไปใช้ไม่ได้แล้ว — เซิร์ฟเวอร์ตอบ
 * เหมือนกันทั้งสองกรณี ข้อความจึงพูดถึงสิ่งที่ผู้ใช้ทำได้จริง คือพิมพ์ใหม่ และ
 * **ไม่พูดถึงการถูกล็อก** เพราะที่นี่ไม่นับ failed attempt
 */
export const WRONG_CURRENT_PASSWORD_MESSAGE =
  'รหัสผ่านปัจจุบันไม่ถูกต้อง — แก้แล้วกดบันทึกใหม่ได้ทันที';

/** ข้อความเฉพาะของแต่ละ `details.rule` — สองกฎนี้ต้องอ่านไม่เหมือนกัน */
function describePolicyRule(rule: string | null): string {
  switch (rule) {
    case PASSWORD_TOO_SHORT_RULE:
      return 'รหัสผ่านใหม่สั้นเกินไปตามนโยบายของเซิร์ฟเวอร์ — ตั้งให้ยาวขึ้นแล้วลองอีกครั้ง';
    case PASSWORD_NEEDS_SPECIAL_CHARACTER_RULE:
      return 'รหัสผ่านใหม่ต้องมีอักขระพิเศษอย่างน้อย 1 ตัว (เช่น ! # % @)';
    case PASSWORD_SAME_AS_CURRENT_RULE:
      return 'รหัสผ่านใหม่ต้องไม่ซ้ำกับรหัสเดิม (รวมถึงรหัสชั่วคราวที่ได้รับจากผู้ดูแลระบบ)';
    case null:
      return 'รหัสผ่านใหม่ไม่ผ่านนโยบายรหัสผ่านของเซิร์ฟเวอร์';
    default:
      // กฎใหม่ที่เซิร์ฟเวอร์เพิ่มภายหลังต้องไม่ถูกกลืนเป็นข้อความกลาง ๆ ที่บอกอะไร
      // ไม่ได้เลย — แสดงชื่อกฎไปตรง ๆ จนกว่าจะมีข้อความของมันเอง
      return `รหัสผ่านใหม่ไม่ผ่านนโยบายรหัสผ่านของเซิร์ฟเวอร์ (${rule})`;
  }
}

/** ข้อความของทุกคำตอบที่ contract ระบุไว้ — แจกแจงครบเพื่อให้ TypeScript พิสูจน์ได้ */
function describeResult(result: PasswordChangeResult): string {
  switch (result.status) {
    case 'rejected':
      return WRONG_CURRENT_PASSWORD_MESSAGE;
    case 'forbidden':
      return 'เซิร์ฟเวอร์ปฏิเสธการเปลี่ยนรหัสผ่านของบัญชีนี้';
    case 'bad-request':
      return 'คำขอเปลี่ยนรหัสผ่านไม่ถูกต้องตามรูปแบบที่เซิร์ฟเวอร์รับ';
    case 'policy-violation':
      return describePolicyRule(result.rule);
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${result.httpStatus} — ยังเปลี่ยนรหัสผ่านไม่ได้`;
    case 'network-error':
      return 'ติดต่อเซิร์ฟเวอร์ไม่ได้ — ยังเปลี่ยนรหัสผ่านไม่ได้';
    case 'changed':
      // ความสำเร็จไม่ได้เรนเดอร์ผ่านกล่องข้อความผิดพลาด — แจกแจงไว้ให้ TypeScript
      // พิสูจน์ว่าครบทุกสถานะ แทนที่จะมี `default` ที่กลืนสถานะใหม่เงียบ ๆ
      return '';
  }
}

export interface ChangePasswordFormProps {
  readonly purpose: PasswordChangePurpose;
  /**
   * token ที่ใช้ยืนยันคำขอนี้ — ผู้เรียกเป็นผู้ถือ
   *
   * เส้นทาง `RECOVERY` ส่ง `password_change_token` ที่**ต้องอยู่ใน state ของ
   * component เท่านั้น** ห้ามเขียนลง `localStorage` (D-049 อนุญาตให้เก็บเฉพาะ
   * session token): token ที่รอดการรีเฟรชคือสถานะกึ่งเข้าสู่ระบบที่เซิร์ฟเวอร์
   * ตั้งใจไม่ออกให้
   */
  readonly token: string;
  /** มาจาก shell ที่กำลังใช้อยู่ ไม่ใช่จากผู้ใช้ — คุมขนาดปุ่ม/ช่องกรอกเท่านั้น */
  readonly deviceKind: DeviceKind;
  /** เรียกเมื่อเซิร์ฟเวอร์ตอบ `204` — ผู้เรียกเป็นคนตัดสินว่าไปต่อที่ไหน */
  readonly onChanged: () => void;
  /** ปุ่มถอยออก (ถ้ามี) — ผู้เรียกต้องทิ้ง token ที่ถืออยู่เมื่อถูกเรียก */
  readonly onCancel?: () => void;
  /** inject ได้เพื่อการทดสอบ — production ใช้ค่าจาก `VITE_API_BASE_URL` */
  readonly apiBaseUrl?: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

export function ChangePasswordForm({
  purpose,
  token,
  deviceKind,
  onChanged,
  onCancel,
  apiBaseUrl,
  fetchImpl,
}: ChangePasswordFormProps) {
  const currentRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<ChangeFailure | null>(null);

  const recovery = purpose === 'RECOVERY';
  const handheld = deviceKind === 'HANDHELD';
  const fieldStyle = handheld ? touchTargetStyle : undefined;

  const clearFields = (refs: Array<HTMLInputElement | null>) => {
    for (const field of refs) {
      if (field !== null) {
        field.value = '';
      }
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    // รหัสผ่านไม่ถูก trim — ช่องว่างหัวท้ายเป็นส่วนหนึ่งของรหัสผ่านที่ตั้งไว้จริง
    const currentPassword = currentRef.current?.value ?? '';
    const newPassword = nextRef.current?.value ?? '';
    const confirmPassword = confirmRef.current?.value ?? '';

    if (currentPassword === '' || newPassword === '' || confirmPassword === '') {
      setFailure({ kind: 'missing-input' });
      return;
    }

    if (newPassword !== confirmPassword) {
      /*
       * ช่องยืนยันเป็นกติกาของหน้าจอล้วน ๆ ไม่เคยถูกส่งไปกับคำขอ (contract รับ
       * สองคีย์เท่านั้น) และมีไว้เพราะรหัสใหม่ที่พิมพ์ผิดบนเส้นทางกู้คืนแปลว่า
       * ผู้ใช้ล็อกตัวเองออกจากระบบจริง ๆ แล้วต้องให้ผู้ดูแลระบบรีเซ็ตใหม่ —
       * ซึ่งคือทางตันที่งานนี้มีไว้เพื่อกำจัด
       *
       * ล้างเฉพาะสองช่องที่ขัดกัน ไม่ล้างรหัสปัจจุบันที่ยังไม่ได้ถูกส่งออกไปไหน
       */
      clearFields([nextRef.current, confirmRef.current]);
      setFailure({ kind: 'mismatch' });
      return;
    }

    setSubmitting(true);
    setFailure(null);

    void changePassword(
      { currentPassword, newPassword },
      {
        token,
        ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
        ...(fetchImpl === undefined ? {} : { fetchImpl }),
      },
    ).then((result) => {
      // ล้างทุกช่องทันทีไม่ว่าผลจะเป็นอย่างไร — คำขอออกไปแล้ว จึงไม่มีเหตุให้
      // รหัสผ่านค้างอยู่ใน DOM ต่อ เหมือนที่ `LoginPage` ทำกับช่องรหัสผ่านของมัน
      clearFields([currentRef.current, nextRef.current, confirmRef.current]);

      if (result.status === 'changed') {
        setSubmitting(false);
        onChanged();
        return;
      }

      setFailure({ kind: 'result', result });
      setSubmitting(false);
    });
  };

  const errorMessage =
    failure === null
      ? null
      : failure.kind === 'missing-input'
        ? MISSING_INPUT_MESSAGE
        : failure.kind === 'mismatch'
          ? MISMATCH_MESSAGE
          : describeResult(failure.result);

  const formLabel = recovery ? 'ตั้งรหัสผ่านใหม่' : 'เปลี่ยนรหัสผ่าน';

  return (
    <section
      className="cap-password-change"
      data-testid="password-change"
      data-purpose={purpose}
      data-device-kind={deviceKind}
    >
      <h2 className="cap-password-change__title">
        {recovery ? 'ตั้งรหัสผ่านใหม่ก่อนเข้าใช้งาน' : 'เปลี่ยนรหัสผ่านของคุณ'}
      </h2>

      <p className="cap-password-change__sub">
        {recovery
          ? 'ผู้ดูแลระบบรีเซ็ตรหัสผ่านของบัญชีนี้ไว้ เซิร์ฟเวอร์จึงยังไม่ออกสิทธิ์การใช้งานให้จนกว่าจะตั้งรหัสผ่านใหม่ที่นี่'
          : 'ต้องใส่รหัสผ่านปัจจุบันเสมอ เพื่อไม่ให้เครื่องที่เปิดค้างไว้ถูกใครก็ได้เปลี่ยนรหัสผ่านของคุณ'}
      </p>

      {/*
        บอกกติกา **ก่อน** ที่เซิร์ฟเวอร์จะปฏิเสธ ไม่ใช่หลังจากนั้น — D-047 ระบุว่า
        นี่เป็นที่เดียวที่ error ของ auth บอกรายละเอียดได้ เพราะผู้ใช้กำลังตั้งรหัส
        ของตัวเอง ไม่ใช่กำลังเดารหัสของคนอื่น
      */}
      <p className="cap-password-change__policy" data-testid="password-change-policy">
        รหัสผ่านใหม่ต้องยาวอย่างน้อย {PASSWORD_MIN_LENGTH_HINT} ตัวอักษร และมีอักขระพิเศษอย่างน้อย
        1 ตัว
      </p>

      {recovery ? (
        <p className="cap-password-change__ending" data-testid="password-change-ending">
          เมื่อตั้งรหัสผ่านใหม่สำเร็จ ระบบจะพากลับไปหน้าเข้าสู่ระบบเพื่อเข้าใช้งานด้วยรหัสผ่านใหม่
        </p>
      ) : null}

      {/*
        `method="post"` ระบุไว้ตรง ๆ แม้จะ `preventDefault()` เสมอ ด้วยเหตุผล
        เดียวกับฟอร์ม login: ฟอร์มที่ถูก submit แบบ GET จะพารหัสผ่านไปอยู่ใน query
        string ของ URL แล้วต่อไปอยู่ใน history และ access log
      */}
      <form
        className="cap-password-change__form"
        method="post"
        onSubmit={submit}
        aria-label={formLabel}
      >
        <div className="cap-password-change__field">
          <label className="cap-password-change__label" htmlFor="cap-password-change-current">
            รหัสผ่านปัจจุบัน
          </label>
          <input
            id="cap-password-change-current"
            ref={currentRef}
            className="cap-password-change__input"
            style={fieldStyle}
            name="current_password"
            type="password"
            autoComplete="current-password"
            disabled={submitting}
          />
        </div>

        <div className="cap-password-change__field">
          <label className="cap-password-change__label" htmlFor="cap-password-change-new">
            รหัสผ่านใหม่
          </label>
          <input
            id="cap-password-change-new"
            ref={nextRef}
            className="cap-password-change__input"
            style={fieldStyle}
            name="new_password"
            type="password"
            autoComplete="new-password"
            disabled={submitting}
          />
        </div>

        <div className="cap-password-change__field">
          <label className="cap-password-change__label" htmlFor="cap-password-change-confirm">
            ยืนยันรหัสผ่านใหม่
          </label>
          <input
            id="cap-password-change-confirm"
            ref={confirmRef}
            className="cap-password-change__input"
            style={fieldStyle}
            // ไม่มี `name` ที่ตรงกับคีย์ของ contract เพราะช่องนี้ไม่เคยถูกส่งออกไป
            name="confirm_new_password"
            type="password"
            autoComplete="new-password"
            disabled={submitting}
          />
        </div>

        {errorMessage === null ? null : (
          <p
            className="cap-password-change__error"
            role="alert"
            data-testid="password-change-error"
          >
            {errorMessage}
          </p>
        )}

        <div className="cap-password-change__actions">
          <button
            type="submit"
            className="cap-password-change__submit"
            style={handheld ? primaryTouchTargetStyle : undefined}
            disabled={submitting}
            data-testid="password-change-submit"
          >
            {submitting ? 'กำลังบันทึก…' : recovery ? 'ตั้งรหัสผ่านใหม่' : 'บันทึกรหัสผ่านใหม่'}
          </button>

          {onCancel === undefined ? null : (
            <button
              type="button"
              className="cap-password-change__cancel"
              style={handheld ? primaryTouchTargetStyle : undefined}
              disabled={submitting}
              data-testid="password-change-cancel"
              onClick={onCancel}
            >
              ยกเลิก
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
